/**
 * RP12 (D14) — Dialogue « Valider et diffuser » : une seule diffusion.
 *
 * Kroquis produit le PDF du compte rendu à partir de son texte au moment de la
 * diffusion ; ce PDF est figé, joint à l'e-mail et servi par la page de
 * consultation. « Remplacer le PDF » reste une option exceptionnelle (dépliée à
 * la demande) ; ce n'est plus un passage obligé.
 *
 * Endpoints :
 * - GET    /chantiers/{cid}/crc/{crc_id}/destinataires   (destinataires proposés + PDF qui partira)
 * - GET    /chantiers/{cid}/crc/{crc_id}/pdf             (aperçu / PDF figé)
 * - POST   /chantiers/{cid}/crc/{crc_id}/remplacer-pdf   (option exceptionnelle)
 * - DELETE /chantiers/{cid}/crc/{crc_id}/remplacer-pdf   (revenir au PDF produit par Kroquis)
 * - POST   /chantiers/{cid}/crc/{crc_id}/fusion-annexes
 * - POST   /chantiers/{cid}/crc/{crc_id}/diffuser         (diffusion unique)
 * - GET    /documents/{cid}
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Users, Paperclip, PaperPlaneTilt, FilePdf, CaretDown, CaretRight, Warning } from '@phosphor-icons/react';
import { libelleRole, libelleCompteRendu, BOUTONS } from '../../../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SendCRCDialog({ chantierId, crc, onClose, onSuccess }) {
  const crcId = crc?.id || crc?._id;
  const [pdfInfo, setPdfInfo] = useState(null);
  const [dejaDiffuse, setDejaDiffuse] = useState(false);
  const [destinataires, setDestinataires] = useState([]);
  const [loadingDest, setLoadingDest] = useState(true);
  const [includeAnnexes, setIncludeAnnexes] = useState(false);
  const [annexeDocIds, setAnnexeDocIds] = useState([]);
  const [availableDocs, setAvailableDocs] = useState([]);
  const [messageCustom, setMessageCustom] = useState('');
  const [showRemplacer, setShowRemplacer] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  // A2 (R4, T1, constat 18) — avertissements avant diffusion (texte incomplet, notes absentes…)
  const [verification, setVerification] = useState([]);
  const [relu, setRelu] = useState(false);

  const chargerDestinataires = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/chantiers/${chantierId}/crc/${crcId}/destinataires`,
        { withCredentials: true },
      );
      setPdfInfo(res.data?.pdf || null);
      setDejaDiffuse(!!res.data?.deja_diffuse);
      setVerification(Array.isArray(res.data?.verification) ? res.data.verification : []);
      return (res.data?.destinataires || []).map((d) => ({
        user_id: d.destinataire_id || '',
        email: d.email,
        nom: d.nom || d.email,
        role: d.role || '',
        entreprise_nom: d.entreprise_nom || '',
        selected: !!d.email,
        convoque_prochaine: true,
      }));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Impossible de charger les destinataires.');
      return [];
    }
  };

  useEffect(() => {
    if (!chantierId || !crcId) return;
    (async () => {
      setLoadingDest(true);
      setDestinataires(await chargerDestinataires());
      setLoadingDest(false);
      try {
        const res = await axios.get(`${API_URL}/api/documents/${chantierId}`, { withCredentials: true });
        setAvailableDocs((res.data || []).filter((d) => (d.content_type || '').toLowerCase() === 'application/pdf'));
      } catch { /* annexes facultatives */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chantierId, crcId]);

  const ouvrirApercu = async () => {
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/chantiers/${chantierId}/crc/${crcId}/pdf`, {
        responseType: 'blob', withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError("L'aperçu du PDF n'a pas pu être produit.");
    }
  };

  const remplacerPdf = async () => {
    if (!pdfFile) return;
    setUploading(true);
    setError(null);
    const fd = new FormData();
    fd.append('file', pdfFile);
    try {
      const res = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/crc/${crcId}/remplacer-pdf`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true },
      );
      setInfo(res.data?.diffuse
        ? 'Le PDF officiel a été remplacé : la page de consultation sert désormais ce PDF.'
        : 'Ce PDF partira à la place de celui produit par Kroquis.');
      setPdfFile(null);
      setShowRemplacer(false);
      await chargerDestinataires();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Le PDF n\'a pas pu être remplacé.');
    } finally {
      setUploading(false);
    }
  };

  const revenirPdfKroquis = async () => {
    setError(null);
    try {
      await axios.delete(`${API_URL}/api/chantiers/${chantierId}/crc/${crcId}/remplacer-pdf`, { withCredentials: true });
      setInfo('Le PDF produit par Kroquis sera joint.');
      await chargerDestinataires();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur');
    }
  };

  const selected = destinataires.filter((d) => d.selected);

  const diffuser = async () => {
    if (selected.length === 0) {
      setError('Sélectionnez au moins un destinataire.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      if (includeAnnexes && annexeDocIds.length > 0) {
        await axios.post(
          `${API_URL}/api/chantiers/${chantierId}/crc/${crcId}/fusion-annexes`,
          { document_ids: annexeDocIds },
          { withCredentials: true },
        );
      }
      const res = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/crc/${crcId}/diffuser`,
        {
          destinataires: selected.map((d) => ({
            user_id: d.user_id,
            nom: d.nom,
            email: d.email,
            role: d.role,
            convoque_prochaine: d.convoque_prochaine,
          })),
          include_annexes: includeAnnexes && annexeDocIds.length > 0,
          message_custom: messageCustom,
        },
        { withCredentials: true },
      );
      onSuccess?.({ ...res.data, sent_count: res.data?.nb_destinataires || selected.length, failed_count: 0 });
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'La diffusion a échoué.');
      setConfirming(false);
    } finally {
      setSending(false);
    }
  };

  const remplace = pdfInfo?.source === 'remplace';
  const numero = crc?.numero;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="send-crc-dialog">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">{BOUTONS.validerEtDiffuser} — {libelleCompteRendu(numero)}</h2>
            {dejaDiffuse && (
              <p className="text-xs text-gray-500" data-testid="send-crc-deja-envoye">
                Ce compte rendu a déjà été diffusé : une nouvelle diffusion renvoie le même PDF figé.
              </p>
            )}
          </div>
          <button onClick={onClose} data-testid="send-crc-close" aria-label="Fermer"><X size={24} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{error}</div>}
          {info && <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800">{info}</div>}
          {verification.length > 0 && !dejaDiffuse && (
            <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm text-amber-900" data-testid="send-crc-verification">
              <p className="font-semibold flex items-center gap-1.5 mb-1"><Warning size={16} /> Avant de diffuser, relisez le compte rendu :</p>
              <ul className="list-disc pl-5 space-y-0.5">
                {verification.map((v) => <li key={v.code + v.message}>{v.message}</li>)}
              </ul>
            </div>
          )}

          {/* 1. PDF officiel */}
          <section data-testid="send-crc-pdf">
            <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <FilePdf size={20} /> 1. PDF du compte rendu
            </h3>
            {remplace ? (
              <p className="text-sm text-gray-700">
                PDF de remplacement : <strong>{pdfInfo?.filename || pdfInfo?.remplacement_filename}</strong>. C'est lui qui sera joint et consultable.
              </p>
            ) : (
              <p className="text-sm text-gray-700">
                Kroquis produit le PDF à partir du texte du compte rendu au moment de la diffusion.
                Ce PDF est figé, joint à l'e-mail et affiché sur la page de consultation.
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <button onClick={ouvrirApercu} className="px-3 py-1.5 border rounded text-sm" data-testid="send-crc-apercu">
                {dejaDiffuse ? 'Voir le PDF diffusé' : 'Aperçu du PDF'}
              </button>
              {remplace && !dejaDiffuse && (
                <button onClick={revenirPdfKroquis} className="px-3 py-1.5 border rounded text-sm" data-testid="send-crc-pdf-kroquis">
                  Revenir au PDF produit par Kroquis
                </button>
              )}
            </div>
            <button
              onClick={() => setShowRemplacer((v) => !v)}
              className="mt-3 text-xs text-gray-500 flex items-center gap-1 hover:text-gray-700"
              data-testid="send-crc-remplacer-toggle"
            >
              {showRemplacer ? <CaretDown size={12} /> : <CaretRight size={12} />}
              Option exceptionnelle : remplacer le PDF
            </button>
            {showRemplacer && (
              <div className="mt-2 border rounded p-3 bg-gray-50 space-y-2">
                <p className="text-xs text-gray-600 flex items-start gap-1">
                  <Warning size={14} className="text-amber-600 shrink-0 mt-0.5" />
                  {dejaDiffuse
                    ? "Le compte rendu est diffusé : le PDF déposé remplacera le PDF officiel sur la page de consultation (l'ancienne version est conservée)."
                    : 'Le PDF déposé partira à la place de celui produit par Kroquis. À réserver aux cas exceptionnels.'}
                </p>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setPdfFile(e.target.files[0])}
                  className="border rounded p-2 w-full text-sm bg-white"
                  data-testid="send-crc-pdf-input"
                />
                <button
                  onClick={remplacerPdf}
                  disabled={!pdfFile || uploading}
                  className="px-3 py-1.5 bg-gray-700 text-white rounded text-sm disabled:opacity-50"
                  data-testid="send-crc-pdf-upload"
                >
                  {uploading ? 'Envoi du PDF…' : 'Remplacer le PDF'}
                </button>
              </div>
            )}
          </section>

          {/* 2. Destinataires */}
          <section>
            <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Users size={20} /> 2. Destinataires ({selected.length}/{destinataires.length})
            </h3>
            <p className="text-xs text-gray-500 mb-2">
              Chaque entreprise reçoit, dans le même e-mail, la liste de ses actions avec un lien par action.
            </p>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs">
                  <tr>
                    <th className="text-left p-2">Inclure</th>
                    <th className="text-left p-2">Nom</th>
                    <th className="text-left p-2">Rôle</th>
                    <th className="text-left p-2">E-mail</th>
                    <th className="text-left p-2">Convoqué à la prochaine réunion</th>
                  </tr>
                </thead>
                <tbody>
                  {!loadingDest && destinataires.length === 0 && (
                    <tr><td colSpan={5} className="p-3 text-center text-gray-500">Aucun destinataire rattaché à ce chantier.</td></tr>
                  )}
                  {destinataires.map((d, idx) => (
                    <tr key={`${d.email}-${idx}`} className="border-t">
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={d.selected}
                          onChange={(e) => {
                            const nd = [...destinataires];
                            nd[idx] = { ...nd[idx], selected: e.target.checked };
                            setDestinataires(nd);
                          }}
                        />
                      </td>
                      <td className="p-2">
                        {d.nom}
                        {d.entreprise_nom && <div className="text-[10px] text-gray-500">Reçoit « Vos actions »</div>}
                      </td>
                      <td className="p-2 text-xs">{libelleRole(d.role)}</td>
                      <td className="p-2 text-xs">{d.email}</td>
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={d.convoque_prochaine}
                          onChange={(e) => {
                            const nd = [...destinataires];
                            nd[idx] = { ...nd[idx], convoque_prochaine: e.target.checked };
                            setDestinataires(nd);
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* 3. Annexes */}
          <section>
            <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Paperclip size={20} /> 3. Annexes (facultatif)
            </h3>
            <label className="flex items-center gap-2 mb-2">
              <input type="checkbox" checked={includeAnnexes} onChange={(e) => setIncludeAnnexes(e.target.checked)} />
              <span className="text-sm">Joindre des documents du chantier, réunis en un seul PDF</span>
            </label>
            {includeAnnexes && (
              <div className="border rounded-lg p-2 max-h-40 overflow-y-auto">
                {availableDocs.length === 0 && (
                  <p className="text-sm text-gray-500">Aucun PDF dans les documents du chantier.</p>
                )}
                {availableDocs.map((doc) => (
                  <label key={doc._id || doc.id} className="flex items-center gap-2 py-1">
                    <input
                      type="checkbox"
                      checked={annexeDocIds.includes(doc._id || doc.id)}
                      onChange={(e) => {
                        const did = doc._id || doc.id;
                        setAnnexeDocIds(e.target.checked ? [...annexeDocIds, did] : annexeDocIds.filter((id) => id !== did));
                      }}
                    />
                    <span className="text-sm">{doc.name || doc.nom}</span>
                  </label>
                ))}
              </div>
            )}
          </section>

          {/* 4. Message */}
          <section>
            <h3 className="font-semibold text-gray-700 mb-2">4. Message complémentaire (facultatif)</h3>
            <textarea
              value={messageCustom}
              onChange={(e) => setMessageCustom(e.target.value)}
              rows={3}
              placeholder="Ce message s'ajoutera à l'e-mail de diffusion…"
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </section>
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex flex-wrap items-center justify-end gap-2">
          {confirming ? (
            <>
              <span className="text-sm text-gray-700 mr-auto" data-testid="send-crc-recap">
                Diffuser à {selected.length} destinataire(s) ? Ils auront 8 jours ouvrés pour réagir ; sans retour, le compte rendu est validé tacitement.
                {verification.length > 0 && !dejaDiffuse && (
                  <label className="flex items-center gap-2 mt-1 text-amber-900" data-testid="send-crc-relu">
                    <input type="checkbox" checked={relu} onChange={(e) => setRelu(e.target.checked)} />
                    J'ai relu le compte rendu : il peut partir tel quel.
                  </label>
                )}
              </span>
              <button onClick={() => setConfirming(false)} className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded">Retour</button>
              <button
                onClick={diffuser}
                disabled={sending || (verification.length > 0 && !dejaDiffuse && !relu)}
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                data-testid="send-crc-confirm"
              >
                <PaperPlaneTilt size={16} />
                {sending ? 'Diffusion…' : 'Confirmer la diffusion'}
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded">Annuler</button>
              <button
                onClick={() => { setError(null); setConfirming(true); }}
                disabled={selected.length === 0 || sending}
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                data-testid="send-crc-submit"
              >
                <PaperPlaneTilt size={16} />
                {BOUTONS.validerEtDiffuser}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
